import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Calendar, Flame, Target, Award, TrendingUp, ArrowLeft } from 'lucide-react';
import ScorecardNavigation from './ScorecardNavigation';
import MonthFilter from './MonthFilter';
import { ServerPerformanceHistoryService, type ServerPerformanceHistoryEntry } from '../../services/ServerPerformanceHistoryService';
import ShiftMetricsTable from './ShiftMetricsTable';
import BenchmarkComparisonBlock from './BenchmarkComparisonBlock';
import { config } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getTenantAvailableMonths } from '../../services/scopedReadsService';
import { dummyServerPerformanceHistoryData } from '../../data/dummyData';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import { useApiError } from '../../hooks/useApiError';
import EstimatedMarker from './pilot/EstimatedMarker';
import { AssistantLauncher } from '../assistant/AssistantLauncher';
import { sphAssistantCopy, sphPageContext } from '../assistant/pages';
import RoleGridHeading from './RoleGridHeading';
import { groupsOf, visibleGroups, showRoleHeadings } from '../../services/scorecardGroups';
import {
  FAKE_M2_TENANT_ID,
  FAKE_MR_TENANT_ID,
  getServerDetail,
  getServerRecentShifts,
  getServerComparison,
  ServerScorecardForbiddenError,
  type ServerDetailResponse,
  type ServerShiftsResponse,
  type ServerComparisonResponse,
  type ServerShiftsGroup,
  type ServerComparisonGroup,
} from '../../services/serverScorecardService';

interface ServerPerformanceHistoryProps {
  serverName?: string;
  overallScore?: number;
}

const ServerPerformanceHistory: React.FC<ServerPerformanceHistoryProps> = ({ 
  serverName = 'John Doe',
  overallScore = 85
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();

  // Get server data from location state or use defaults
  const serverData = location.state?.serverData || { serverName, overallScore };

  // feat-133 Batch-1: tenant is sourced from AUTH CONTEXT ONLY (never the
  // client-supplied serverData.tenantId route-state) so the scope is provably
  // server-bound. Manager/Corporate always have it (TenantContext pins them);
  // an Admin has it once they pick a tenant (required to reach this page). When
  // absent the available-months set is empty — the accepted degrade per ADR D1.
  const scopedTenantId: string | undefined =
    selectedTenant || user?.managementUser?.tenant_id || undefined;

  // feat-234 / QA N1 — fake mode only. The tenant normally arrives in route
  // state from the Scorecard's "View Details", which is how `?tenant=fake-m2`
  // reaches this page on the click path. This covers DIRECT navigation to
  // `/restaurant/server-performance-history?tenant=fake-m2`, so the multiplier-2
  // shape is honest however a reviewer gets here. Ignored in every other
  // environment, where the tenant is resolved from auth and must never be
  // client-supplied.
  const fakeTenantOverride: string | undefined = useMemo(() => {
    if (config.environment !== 'fake') return undefined;
    const requested = new URLSearchParams(location.search).get('tenant');
    // feat-317: `?tenant=fake-mr` is the two-role tenant (one grid per role).
    return requested === FAKE_M2_TENANT_ID || requested === FAKE_MR_TENANT_ID ? requested : undefined;
  }, [location.search]);

  // feat-091: Recent Shifts is now driven by the per-shift listing endpoint
  // (frozen-snapshot scores + full prompt-configured columns), not the raw-SQL
  // /admin/execute-select path. `allTimeHistory` feeds ONE header stat — the
  // Avg. Upsells fallback used when the aggregate detail payload is absent.
  //
  // STRUCK: this said "the header stat cards (avg score / avg upsells)". There
  // is no avg-score card on this page and, since feat-234, no avg-score
  // computation either. THIS EXACT FALSE CLAIM, in three places in this file,
  // is what convinced an approved spec that an "Avg Score" card existed and
  // sent an implementer looking for it. This was the earliest of the three and
  // therefore the first thing a reader scrolling the file meets.
  const [recentShifts, setRecentShifts] = useState<ServerShiftsResponse | null>(null);
  // feat-097 Part D — benchmark↔post comparison (windows are backend PERIOD CONFIG,
  // independent of the month picker). Its own loading flag drives the block's skeleton.
  const [comparison, setComparison] = useState<ServerComparisonResponse | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState<boolean>(true);
  const [allTimeHistory, setAllTimeHistory] = useState<ServerPerformanceHistoryEntry[]>([]);
  const [locationName, setLocationName] = useState<string>('');
  const [mobileStats, setMobileStats] = useState({
    totalXP: 0,
    totalShifts: 0,
    currentStreak: 0,
    longestStreak: 0,
    badgesEarned: 0,
    badgesTotal: 0,
    challengeTitle: '',
    challengeCurrent: 0,
    challengeTarget: 0,
    challengeCompleted: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { error: apiError, handleError: handleApiError, clearError: clearApiError } = useApiError();

  // feat-053d: the aggregate Server-detail payload (same sources as the grid).
  // Real all-time stats (XP/Shifts/Streak) + Avg-Upsells render clean; Challenges,
  // Badges and Avg-Tip-% are seeded placeholders carrying `*_placeholder` flags so
  // the Slate EstimatedMarker can render. Falls back to the mobile-profile stats
  // below when unavailable (e.g. direct navigation without a server_id).
  const [detail, setDetail] = useState<ServerDetailResponse | null>(null);

  // Get available months from performance history data
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  
  // Convert YYYY-MM format to "Month, Year" format for display
  const convertToDisplayFormat = (monthStr: string): string => {
    if (monthStr === 'All') return 'All';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  // Convert "Month, Year" format back to YYYY-MM format for internal use
  const convertFromDisplayFormat = (displayStr: string): string => {
    if (displayStr === 'All') return 'All';
    const date = new Date(displayStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };
  
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      if (config.useDummyData) {
        setAvailableMonths(['2024-09', '2024-08', '2024-07']);
        return;
      }
      
      // Need a server key and a tenant scope to resolve the endpoint.
      if (!serverData.serverName && !serverData.serverId) {
        return;
      }
      if (!scopedTenantId) {
        setAvailableMonths([]);
        return;
      }

      try {
        // feat-133 Batch-1: scoped ref-lookup replaces the execute-select
        // available-months query. Prefer the (globally-unique, tenant-ownership
        // checked) server_id; fall back to server_name when the id is absent —
        // both are bound + tenant-scoped server-side. The backend preserves the
        // exact predicate (US/Pacific bucketing, verified-interaction join,
        // HAVING COUNT>0) so the month set is identical.
        const response = await getTenantAvailableMonths(scopedTenantId, {
          serverId: serverData.serverId || undefined,
          serverName: serverData.serverId ? undefined : serverData.serverName,
        });
        if (response && response.length > 0) {
          const months = response.map((row) => {
            const year = row.year;
            const month = String(row.month).padStart(2, '0');
            return `${year}-${month}`;
          });
          setAvailableMonths(months);
        } else {
          setAvailableMonths([]);
        }
      } catch (error) {
        console.error('ServerPerformanceHistory: Error fetching available months:', error);
        // No fallback - just set empty array if we can't fetch data
        setAvailableMonths([]);
      }
    };

    fetchAvailableMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverData.serverName, serverData.serverId, scopedTenantId]);

  // feat-133 Batch-1: the location name now comes from the aggregate
  // getServerDetail payload (which already carries `location_name`) instead of a
  // separate execute-select lookup by server name. When serverId+tenantId are
  // absent (direct navigation) `detail` is null and the location label stays
  // empty — the accepted degrade per ADR 0001 D1.
  useEffect(() => {
    if (detail?.location_name) {
      setLocationName(detail.location_name);
    }
  }, [detail]);

  // Fetch the same mobile Profile data the Flutter app uses so this page's
  // All-Time Stats (XP, shifts, streak, challenge, badges) match the app exactly.
  useEffect(() => {
    const fetchMobileStats = async () => {
      // feat-133 Batch-4: keyed by the nav serverId — the by-name lookup is gone.
      if (!serverData.serverId) return;
      try {
        const stats = await ServerPerformanceHistoryService.getServerMobileStats(
          serverData.serverId
        );
        setMobileStats(stats);
      } catch (err) {
        console.warn('ServerPerformanceHistory: failed to fetch mobile stats', err);
      }
    };
    fetchMobileStats();
  }, [serverData.serverId]);

  // feat-053d: fetch the aggregate Server-detail payload (seeded Challenges/
  // Badges/Avg-Tip + real Avg-Upsells, all flagged). All-time framing → no window.
  useEffect(() => {
    const fetchDetail = async () => {
      const serverId: string | undefined = serverData.serverId;
      const tenantId: string | undefined = serverData.tenantId || fakeTenantOverride;
      // Fake mode keys off the ENVIRONMENT (config.useDummyData is false in every
      // config); getServerDetail returns canned data so the screen renders.
      if (config.environment === 'fake') {
        setDetail(await getServerDetail(tenantId || 'fake', serverId || 's-alex'));
        return;
      }
      // Need both ids to call the tenant-scoped endpoint; otherwise fall back to
      // the mobile-profile stats (placeholders render with no value, honestly).
      if (!serverId || !tenantId) {
        setDetail(null);
        return;
      }
      try {
        setDetail(await getServerDetail(tenantId, serverId));
      } catch (err) {
        console.warn('ServerPerformanceHistory: failed to fetch server detail', err);
        setDetail(null);
      }
    };
    fetchDetail();
  }, [serverData.serverId, serverData.tenantId]);

  // Fetch all-time shift history for stats (real data) — used for Total Shifts, Avg. Upsells
  useEffect(() => {
    const fetchAllTime = async () => {
      if (config.useDummyData) {
        setAllTimeHistory(dummyServerPerformanceHistoryData);
        return;
      }
      // feat-133 Batch-4: keyed by nav serverId + auth-context tenant (scopedTenantId).
      if (!serverData.serverId || !scopedTenantId) return;
      try {
        const data = await ServerPerformanceHistoryService.getServerPerformanceHistory(
          serverData.serverId,
          scopedTenantId,
          '2020-01-01',
          '2030-12-31'
        );
        setAllTimeHistory(data);
      } catch (err) {
        console.warn('ServerPerformanceHistory: failed to fetch all-time history', err);
      }
    };
    fetchAllTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverData.serverId, scopedTenantId]);

  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    // Restore from navigation state if available (avoids race with fetch effect)
    const savedMonth = location.state?.filters?.selectedMonth;
    if (savedMonth) return savedMonth;

    if (config.useDummyData) {
      return '2024-09';
    }

    // For real data, we'll detect the latest month with data dynamically
    // Start with current month as fallback
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [userHasSelectedMonth, setUserHasSelectedMonth] = useState<boolean>(
    () => !!location.state?.filters?.selectedMonth
  );

  // feat-234 removed the Recent Shifts "#"/"%" toggle (and the shared
  // `useMetricDisplayMode` hook behind it). Every metric cell is now the backend's
  // COUNT. The per-visit `percent` stays on RecentShiftMetricValue and is still
  // computed — deliberately reversible — it is simply no longer rendered.
  // A stale `scorecardMetricDisplayMode` value left in a user's localStorage is
  // harmless and needs no migration: nothing reads that key any more.

  // feat-091: fetch the Recent Shifts listing from the per-shift endpoint. The
  // backend returns the full prompt-configured column set (in display_order, with
  // the ★ scoring flag) plus per-shift frozen values + a ready-to-render Score
  // tooltip — the frontend renders columns in returned order and every value
  // verbatim (metric-single-source, AC#11). Replaces the raw-SQL path.
  useEffect(() => {
    const fetchRecentShifts = async () => {
      const serverId: string | undefined = serverData.serverId;
      const tenantId: string | undefined = serverData.tenantId || fakeTenantOverride;
      const isFake = config.environment === 'fake';

      // The endpoint is tenant+server scoped (path params). Without both ids
      // (e.g. direct navigation) we can't call it — render an empty table rather
      // than an error wall.
      if (!isFake && (!serverId || !tenantId)) {
        setRecentShifts(null);
        setIsLoading(false);
        return;
      }
      if (!selectedMonth) {
        setIsLoading(false);
        return;
      }

      // selectedMonth → inclusive [startDate, endDate]; "All" → wide range.
      let startDate: string, endDate: string;
      if (selectedMonth === 'All') {
        startDate = '2020-01-01';
        endDate = '2030-12-31';
      } else {
        const [year, month] = selectedMonth.split('-');
        startDate = `${year}-${month}-01`;
        const yearInt = parseInt(year, 10);
        const monthInt = parseInt(month, 10);
        endDate = (monthInt === 12
          ? new Date(yearInt + 1, 0, 0)
          : new Date(yearInt, monthInt, 0)
        ).toISOString().split('T')[0];
      }

      setIsLoading(true);
      try {
        setError(null);
        clearApiError();
        const resp = await getServerRecentShifts(tenantId || 'fake', serverId || 's-alex', {
          startDate,
          endDate,
        });
        setRecentShifts(resp);
      } catch (err) {
        if (err instanceof ServerScorecardForbiddenError) {
          // Fail-closed tenant/server scoping — show an empty table, not an error.
          setRecentShifts({
            server_id: serverId || '',
            name: serverData.serverName || '',
            metric_columns: [],
            drink_column: null,
            rows: [],
          });
        } else {
          const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching recent shifts';
          setError(errorMessage);
          handleApiError(err);
          console.error('Error fetching recent shifts:', err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentShifts();
  }, [serverData.serverId, serverData.tenantId, serverData.serverName, selectedMonth, clearApiError, handleApiError]);

  // feat-097 Part D — fetch the benchmark↔post comparison. The two windows come
  // from backend PERIOD CONFIG (no params), so this is INDEPENDENT of the month
  // picker — it does not re-fetch when `selectedMonth` changes. The block is
  // supplementary: on any failure (fail-closed scoping, missing ids) we render
  // nothing rather than block the page.
  useEffect(() => {
    const fetchComparison = async () => {
      const serverId: string | undefined = serverData.serverId;
      const tenantId: string | undefined = serverData.tenantId || fakeTenantOverride;
      const isFake = config.environment === 'fake';
      if (!isFake && (!serverId || !tenantId)) {
        setComparison(null);
        setComparisonLoading(false);
        return;
      }
      setComparisonLoading(true);
      try {
        const resp = await getServerComparison(tenantId || 'fake', serverId || 's-alex');
        setComparison(resp);
      } catch (err) {
        // Supplementary block — degrade to hidden, never an error wall.
        setComparison(null);
      } finally {
        setComparisonLoading(false);
      }
    };
    fetchComparison();
  }, [serverData.serverId, serverData.tenantId]);

  // Update selected month when available months change (only if user hasn't manually selected)
  useEffect(() => {
    if (availableMonths.length > 0 && !userHasSelectedMonth) {
      const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

      setSelectedMonth(prevSelectedMonth => {
        if (!availableMonths.includes(prevSelectedMonth) || prevSelectedMonth === currentMonth) {
          return availableMonths[0];
        }
        return prevSelectedMonth;
      });
    }
  }, [availableMonths, userHasSelectedMonth]);

  // feat-317 (ADR §7.3) — one Recent Shifts grid and one benchmark card per
  // effective role, rendered verbatim in response order. An old backend (no
  // `groups`) synthesises one legacy group → today's markup, no heading.
  const recentShiftGroups: ServerShiftsGroup[] = useMemo(
    () =>
      recentShifts
        ? groupsOf(recentShifts, (r): ServerShiftsGroup => ({
            role: null,
            display_name: null,
            server_id: r.server_id,
            name: r.name,
            metric_columns: r.metric_columns,
            drink_column: r.drink_column,
            rows: r.rows,
          }))
        : [],
    [recentShifts],
  );
  // A role present in neither window gets no card (design NOTES §4.2). If that
  // leaves nothing, the first group renders once → today's State 4, no heading.
  const comparisonGroups: ServerComparisonGroup[] = useMemo(
    () =>
      comparison
        ? visibleGroups(
            groupsOf(comparison, (c): ServerComparisonGroup => ({
              ...c,
              groups: undefined,
              role: null,
              display_name: null,
            } as ServerComparisonGroup)),
            (g) =>
              g.windows.benchmark.shift_count > 0 || g.windows.post.shift_count > 0 ? [g] : [],
          )
        : [],
    [comparison],
  );

  // ─── Derived stats for the dashboard header ─────────────────────────────
  // Real data: avg upsells/shift. Everything else (XP, shifts, streak, challenge,
  // badges) comes from the same mobile endpoints the Flutter app uses, so the two
  // surfaces agree.
  //
  // feat-234: this comment used to read "avg upsells/shift, avg score, level
  // tier". Both extra items were wrong. There is no avg-score stat here — the
  // computation that existed was never rendered — and the level tier is derived
  // from total XP, not from any score. That false comment is what made a reader
  // believe an "Avg Score" card existed on this page; it is corrected rather
  // than deleted so the next person doesn't re-derive the same mistake.
  const headerStats = useMemo(() => {
    const shifts = allTimeHistory ?? [];

    // Local fallback for Avg. Upsells when the aggregate detail payload is absent
    // (avg of metric_1/2/3 + drink detection counts per shift). The endpoint's
    // backend value (scorecard.py metric_counts) is preferred — single source.
    const upsellsPerShift = shifts.map(s => {
      const ra = s.revelAttribution;
      if (!ra) return 0;
      return (
        (ra.metric_1?.upsellCount || 0) +
        (ra.metric_2?.upsellCount || 0) +
        (ra.metric_3?.upsellCount || 0) +
        (ra.drink_promotion?.upsellCount || 0)
      );
    });
    const computedAvgUpsells = upsellsPerShift.length > 0
      ? upsellsPerShift.reduce((a, b) => a + b, 0) / upsellsPerShift.length
      : 0;

    // feat-234: the React-side average score is DELETED. It summed `s.score` over
    // the fetched shifts and divided by their count — an unweighted mean, the exact
    // formula this feature removed, and a second definition of "the score" living
    // in React. The backend's `detail.score_display` is the single source; it is
    // the visit-weighted pooled figure and it is already on this payload.
    //
    // Its result was never rendered — no revision of this component ever read it
    // (`git log -S'headerStats.avgScore'` over this file: zero commits), and the
    // comment that used to sit here claiming it "drives level tier" was false:
    // the tier badge renders `level`, computed from total XP below, never from a
    // score. Deleting it therefore changes nothing on screen.

    // REAL all-time stats — prefer the aggregate detail payload (same sources as
    // the grid), fall back to the mobile-profile stats. Both are lifetime.
    const totalXP = detail?.total_xp ?? mobileStats?.totalXP ?? 0;
    const totalShifts = detail?.total_shifts ?? mobileStats?.totalShifts ?? 0;
    const streak = detail?.current_streak ?? mobileStats?.currentStreak ?? 0;

    // Level from total XP — matches the mobile app's formula
    // (lib/screens/profile_screen.dart: floor(totalXp / 200) + 1)
    const level = Math.floor((totalXP || 0) / 200) + 1;

    // Avg. Upsells — REAL. Prefer the backend-formatted display verbatim.
    const avgUpsellsDisplay = detail?.avg_upsells_display
      ?? (shifts.length > 0 ? computedAvgUpsells.toFixed(1) : '—');

    // Avg. Tip % — PLACEHOLDER. NEVER React-computed, NEVER wired to tip_rate
    // (DECISIONS §4). Render the backend's seeded display verbatim; "—" if absent.
    const avgTipDisplay = detail?.avg_tip_pct_display ?? '—';

    // Challenges + Badges — seeded PLACEHOLDER from the detail payload (A1).
    const hasDetail = detail != null;

    return {
      totalShifts,
      avgUpsellsDisplay,
      level,
      totalXP,
      streak,
      avgTipDisplay,
      hasDetail,
      challengesDone: detail?.challenges_completed ?? null,
      challengesTotal: detail?.challenges_total ?? null,
      badgesEarned: detail?.badges_earned ?? null,
      badgesTotal: detail?.badges_total ?? null,
      // feat-053e2: Badges went REAL on the backend (badges_placeholder:false,
      // badges_data_source:"real"). Default true so the honest fallback when
      // detail is absent stays the Slate placeholder treatment.
      badgesPlaceholder: detail?.badges_placeholder ?? true,
      badgesDataSource: detail?.badges_data_source ?? 'seeded_badges',
    };
    // feat-234: `serverData.overallScore` left this dep list with the deleted
    // React-side average — it was that calculation's only reader.
  }, [allTimeHistory, mobileStats, detail]);

  const handleBackToScorecard = () => {
    // Restore the filter state that was saved when navigating to details
    const savedFilters = location.state?.filters;
    navigate('/restaurant/server-scorecard', {
      state: {
        filters: savedFilters || {}
      }
    });
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    setUserHasSelectedMonth(true);
  };

  // ─── feat-305: manager assistant ────────────────────────────────────────
  // Tenant from AUTH CONTEXT only (feat-133: `scopedTenantId`, never route-state
  // `serverData.tenantId`); server from route state. Either missing → no context
  // → the launcher renders nothing. Dates are the selected month's bounds, or
  // null for "All". The launcher renders nothing unless the tenant is enabled,
  // and nothing on a config failure, so this page is otherwise unchanged.
  //
  // The launcher is mounted as a KEYED child of the page root in every branch
  // (loading / error / loaded), so the month picker's full-page loading state
  // doesn't unmount it: the panel stays open and an answer keeps streaming.
  // Its band button renders into a slot inside the header band.
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantEntry, setAssistantEntry] = useState<HTMLElement | null>(null);
  const assistantContext = useMemo(() => {
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (selectedMonth && selectedMonth !== 'All') {
      const [y, m] = selectedMonth.split('-').map((v) => parseInt(v, 10));
      if (y && m) {
        const mm = String(m).padStart(2, '0');
        startDate = `${y}-${mm}-01`;
        endDate = `${y}-${mm}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
      }
    }
    return sphPageContext({ tenantId: scopedTenantId, serverId: serverData.serverId, startDate, endDate });
  }, [scopedTenantId, serverData.serverId, selectedMonth]);
  const assistantCopy = useMemo(
    () => sphAssistantCopy(serverData.serverName, locationName),
    [serverData.serverName, locationName],
  );
  const assistantLauncher = (
    <AssistantLauncher
      key="feat-305-assistant"
      tenantId={scopedTenantId}
      pageContext={assistantContext}
      copy={assistantCopy}
      onOpenChange={setAssistantOpen}
      entryContainer={assistantEntry}
    />
  );
  // NOTES §2: the page reflows beside the docked panel (≥ lg); below lg the sheet overlays.
  const assistantReflow = `transition-[padding] duration-200 motion-reduce:transition-none ${
    assistantOpen ? 'lg:pr-[380px] xl:pr-[420px]' : ''
  }`;

  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        {assistantLauncher}
        <div className={`p-6 ${assistantReflow}`}>
          <LoadingSpinner message="Loading performance history..." />
        </div>
      </div>
    );
  }

  // Handle error state
  if (error && !config.useDummyData) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        {assistantLauncher}
        <div className={`p-6 ${assistantReflow}`}>
          <ErrorMessage
            title="Unable to Load Performance History"
            message="We're having trouble loading the performance history data. This could be due to database connectivity issues."
            details={error}
            onRetry={() => {
              setError(null);
              window.location.reload();
            }}
            onGoBack={() => navigate('/restaurant/server-scorecard')}
          />
        </div>
      </div>
    );
  }

  const serverInitial = (serverData.serverName || 'S').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      {assistantLauncher}

      <div className={assistantReflow}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={handleBackToScorecard}
          className="inline-flex items-center gap-2 text-inkMuted hover:text-ink text-sm font-medium transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Server Header Band */}
        <div className="relative overflow-hidden rounded-[28px] bg-coral text-white shadow-lg p-8 mb-10">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-md">
              <span className="text-3xl font-display font-bold text-coral">{serverInitial}</span>
            </div>
            <h1 className="text-3xl font-display font-bold mt-4">
              {serverData.serverName}
            </h1>
            <p className="text-white/85 text-sm mt-1">
              Server{locationName ? ` at ${locationName}` : ''}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 text-sm font-semibold">
              <Trophy className="w-4 h-4" />
              Level {headerStats.level}
            </div>
            {/* feat-305: slot for the "Ask about this server" control (empty unless the tenant has the assistant). */}
            <div ref={setAssistantEntry} className="contents" />
          </div>
        </div>

        {/* All-Time Stats */}
        <section className="mb-10">
          <h2 className="text-xl font-display font-semibold text-ink mb-4">All-Time Stats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              icon={<Trophy className="w-5 h-5 text-gold" />}
              iconBg="bg-gold-light"
              label="Total XP"
              value={headerStats.totalXP.toLocaleString()}
              sublabel="All time"
              sublabelColor="text-inkMuted"
            />
            <StatCard
              icon={<Calendar className="w-5 h-5 text-[var(--blue)]" />}
              iconBg="bg-[var(--blue-light)]"
              label="Total Shifts"
              value={headerStats.totalShifts.toString()}
              sublabel="All time"
            />
            <StatCard
              icon={<Flame className="w-5 h-5 text-coral" />}
              iconBg="bg-coral-light"
              label="Current Streak"
              value={`${headerStats.streak} ${headerStats.streak === 1 ? 'day' : 'days'}`}
              sublabel={headerStats.streak > 0 ? 'Keep it going!' : 'Start a new streak'}
            />
            {/* Challenges — seeded PLACEHOLDER (Slate marker, ADR D3) */}
            <StatCard
              icon={<Target className="w-5 h-5 text-preview" />}
              iconBg="bg-preview-light"
              label="Challenges"
              value={
                headerStats.challengesTotal != null
                  ? `${headerStats.challengesDone}/${headerStats.challengesTotal}`
                  : '—'
              }
              sublabel={
                headerStats.challengesTotal != null
                  ? `${Math.round(((headerStats.challengesDone || 0) / headerStats.challengesTotal) * 100)}% complete · seeded`
                  : 'Not yet measured'
              }
              estimated
            />
            {/* Badges — flag-driven (feat-053e2). When the backend reports
                badges_placeholder:false the card renders CLEAN (real green icon,
                no "Estimated" chip, no "· seeded" sublabel); when it still reports
                placeholder:true we keep the Slate marker treatment (Amendment A1). */}
            <StatCard
              icon={
                <Award
                  className={`w-5 h-5 ${headerStats.badgesPlaceholder ? 'text-preview' : 'text-[var(--green)]'}`}
                />
              }
              iconBg={headerStats.badgesPlaceholder ? 'bg-preview-light' : 'bg-[var(--green-light)]'}
              label="Badges"
              value={
                headerStats.badgesTotal != null
                  ? `${headerStats.badgesEarned}/${headerStats.badgesTotal}`
                  : '—'
              }
              sublabel={
                headerStats.badgesTotal != null
                  ? `${Math.max(0, headerStats.badgesTotal - (headerStats.badgesEarned || 0))} remaining${headerStats.badgesPlaceholder ? ' · seeded' : ''}`
                  : 'Not yet measured'
              }
              estimated={!!headerStats.badgesPlaceholder}
            />
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="mb-10">
          <h2 className="text-xl font-display font-semibold text-ink mb-4">Performance Metrics</h2>
          <div className="space-y-3">
            {/* Avg. Tip % — PLACEHOLDER (Slate). NEVER wired to tip_rate (DECISIONS §4);
                no success-green delta — a seeded value must not read as measured. */}
            <MetricRow
              icon={<TrendingUp className="w-5 h-5 text-preview" />}
              iconBg="bg-preview-light"
              label="Avg. Tip %"
              sublabel="Not yet measured — we don't track tips"
              value={headerStats.avgTipDisplay}
              estimated
            />
            {/* Avg. Upsells — REAL (scorecard.py upsell counts). Clean, no fabricated delta. */}
            <MetricRow
              icon={<TrendingUp className="w-5 h-5 text-success" />}
              iconBg="bg-success-light"
              label="Avg. Upsells"
              sublabel="Per shift · measured"
              value={headerStats.avgUpsellsDisplay}
            />
          </div>
        </section>

        {/* feat-097 Part D — benchmark↔post comparison, directly above Recent Shifts */}
        <section className="mb-8">
          {!comparisonLoading && showRoleHeadings(comparisonGroups) ? (
            <div className="space-y-6">
              {comparisonGroups.map((g, i) => (
                <div key={g.role ?? `group-${i}`}>
                  <RoleGridHeading as="h3" displayName={g.display_name} role={g.role} />
                  <BenchmarkComparisonBlock data={g} />
                </div>
              ))}
            </div>
          ) : (
            <BenchmarkComparisonBlock
              data={comparisonGroups[0] ?? comparison}
              loading={comparisonLoading}
            />
          )}
        </section>

        {/* Recent Shifts */}
        <section>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl font-display font-semibold text-ink">Recent Shifts</h2>
              <p className="text-sm text-inkMuted mt-0.5">
                Complete shift history for {serverData.serverName}
              </p>
            </div>
            {/* feat-234: the #/% toggle that sat here is deleted. The wrapper stays —
                it uses `gap-3`, so the surviving child needs no spacing fix, and it
                shrink-wraps to the button under the parent's `items-end`. */}
            <div className="flex items-center gap-3">
              <MonthFilter
                availableMonths={availableMonths.map(convertToDisplayFormat)}
                selectedMonth={convertToDisplayFormat(selectedMonth)}
                onMonthChange={(displayMonth) => handleMonthChange(convertFromDisplayFormat(displayMonth))}
              />
            </div>
          </div>

          {/* Warning banner when using sample data */}
          {error && config.useDummyData && (
            <div className="mb-4 p-4 bg-gold-light border border-gold/40 rounded-2xl">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-gold" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-ink">Using Sample Data</h3>
                  <p className="mt-1 text-sm text-inkMuted">
                    Database connection failed. Showing sample data for development purposes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showRoleHeadings(recentShiftGroups) ? (
            <div className="space-y-6">
              {recentShiftGroups.map((g, i) => (
                <div key={g.role ?? `group-${i}`}>
                  <RoleGridHeading as="h3" displayName={g.display_name} role={g.role} />
                  <div className="bg-white rounded-[20px] shadow-lg overflow-hidden">
                    <ShiftMetricsTable
                      recentShifts={{
                        metricColumns: g.metric_columns,
                        drinkColumn: g.drink_column ?? null,
                        rows: g.rows,
                        emptyMessage: 'No shift data available for the selected month.',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-[20px] shadow-lg overflow-hidden">
              <ShiftMetricsTable
                recentShifts={{
                  metricColumns: recentShiftGroups[0]?.metric_columns ?? [],
                  drinkColumn: recentShiftGroups[0]?.drink_column ?? null,
                  rows: recentShiftGroups[0]?.rows ?? [],
                  emptyMessage: 'No shift data available for the selected month.',
                }}
              />
            </div>
          )}
        </section>
      </div>
      </div>
    </div>
  );
};

// ─── Presentational subcomponents ───────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  sublabel?: string;
  sublabelColor?: string;
  /** Seeded placeholder treatment: preview-border card, "Estimated" chip, slate value. */
  estimated?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, label, value, sublabel, sublabelColor = 'text-inkMuted', estimated = false }) => (
  <div className={`bg-white rounded-2xl shadow-sm p-5 border ${estimated ? 'border-preview-border' : 'border-softBorder'}`}>
    <div className="flex items-center justify-between mb-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
      {estimated && <EstimatedMarker variant="chip" />}
    </div>
    <div className="text-sm text-inkMuted mb-1">{label}</div>
    <div className={`text-3xl font-display font-semibold ${estimated ? 'text-preview' : 'text-ink'}`}>{value}</div>
    {sublabel && <div className={`text-xs mt-1 ${sublabelColor}`}>{sublabel}</div>}
  </div>
);

interface MetricRowProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  sublabel?: string;
  value: string;
  /** Optional change indicator. Real metrics → success-green; never for seeded. */
  delta?: string;
  /** Seeded placeholder treatment: preview-border row, "Estimated" chip, slate value. */
  estimated?: boolean;
}

const MetricRow: React.FC<MetricRowProps> = ({ icon, iconBg, label, sublabel, value, delta, estimated = false }) => (
  <div className={`bg-white rounded-2xl shadow-sm px-5 py-4 flex items-center justify-between border ${estimated ? 'border-preview-border' : 'border-softBorder'}`}>
    <div className="flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-ink flex items-center gap-1.5">
          {label}
          {estimated && <EstimatedMarker variant="chip" />}
        </div>
        {sublabel && <div className="text-xs text-inkMuted">{sublabel}</div>}
      </div>
    </div>
    <div className="text-right">
      <div className={`text-2xl font-display font-semibold ${estimated ? 'text-preview' : 'text-ink'}`}>{value}</div>
      {/* Real positive change → success-green. Seeded deltas are forbidden here
          (would read as measured — the feat-053b honesty rule). */}
      {delta && !estimated && <div className="text-xs text-success font-semibold">{delta}</div>}
    </div>
  </div>
);

export default ServerPerformanceHistory;
